import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getProductionJobs, getProductionJob, advanceProdJobStage, updateProdJobStage, uploadJobStageFile, getMe } from '../services/api';
import { STAGES } from '../components/prodmgmt/stages';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const stageLabel = (key) => { const s = STAGES.find((x) => x.key === key); return s ? s.label : key; };
const stageDef = (key) => STAGES.find((x) => x.key === key);

function OperatorPage() {
  const [view, setView] = useState('login');
  const [token, setToken] = useState(localStorage.getItem('token') || '');
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
      setView('queue');
    }
  }, [token]);

  const handleLogin = (e) => {
    e.preventDefault();
    setErr('');
    fetch(API_BASE + '/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password: pass }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.access_token) {
          localStorage.setItem('token', data.access_token);
          axios.defaults.headers.common['Authorization'] = 'Bearer ' + data.access_token;
          setToken(data.access_token);
          setView('queue');
        } else {
          setErr(data.detail || 'Login failed');
        }
      })
      .catch(() => setErr('Connection error'));
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken('');
    setView('login');
  };

  if (view === 'login') {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <div className="w-14 h-14 bg-[#FF6600] rounded-xl flex items-center justify-center mx-auto mb-3">
              <span className="text-white text-xl font-black">FE</span>
            </div>
            <h1 className="text-xl font-bold text-white">Operator Portal</h1>
            <p className="text-sm text-gray-400 mt-1">Factory Floor Access</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4" data-testid="operator-login-form">
            <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-[#2a2a2a] border border-gray-700 text-white placeholder-gray-500 text-base" required data-testid="operator-email" />
            <input type="password" placeholder="Password" value={pass} onChange={(e) => setPass(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-[#2a2a2a] border border-gray-700 text-white placeholder-gray-500 text-base" required data-testid="operator-password" />
            {err && <p className="text-red-400 text-sm text-center">{err}</p>}
            <button type="submit" className="w-full py-3 rounded-lg bg-[#FF6600] text-white font-bold text-base active:scale-95 transition-transform" data-testid="operator-login-btn">Sign In</button>
          </form>
        </div>
      </div>
    );
  }

  return <WorkQueue onLogout={handleLogout} />;
}

function WorkQueue({ onLogout }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [operatorName, setOperatorName] = useState('');
  const [assignedStages, setAssignedStages] = useState([]);

  const fetchData = useCallback(() => {
    getProductionJobs()
      .then((res) => { setJobs((res.data || []).filter((j) => j.status !== 'completed')); setLoading(false); })
      .catch(() => { setJobs([]); setLoading(false); });
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    const iv = setInterval(fetchData, 30000);
    return () => clearInterval(iv);
  }, [fetchData]);
  useEffect(() => { getMe().then((r) => { setOperatorName(r.data.full_name || ''); setAssignedStages(r.data.assigned_stages || []); }).catch(() => {}); }, []);

  if (selectedId) {
    return <JobPanel jobId={selectedId} operatorName={operatorName} onBack={() => { setSelectedId(null); fetchData(); }} />;
  }

  const visibleJobs = assignedStages.length ? jobs.filter((j) => assignedStages.includes(j.current_stage)) : jobs;

  return (
    <div className="min-h-screen bg-[#1a1a1a] text-white">
      <div className="sticky top-0 z-10 bg-[#1a1a1a] border-b border-gray-800 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#FF6600] rounded-lg flex items-center justify-center"><span className="text-white text-xs font-black">FE</span></div>
          <span className="font-bold text-sm">Floor Operator</span>
        </div>
        <button onClick={onLogout} className="text-xs text-gray-400 px-3 py-1.5 border border-gray-700 rounded-lg active:bg-gray-800" data-testid="operator-logout">Logout</button>
      </div>

      <div className="px-4 py-4">
        <h2 className="text-sm font-medium text-gray-400 mb-1">Active Jobs</h2>
        {assignedStages.length > 0 && <p className="text-[11px] text-gray-500 mb-3" data-testid="op-assigned-note">Showing only your assigned stage(s): {assignedStages.map(stageLabel).join(', ')}</p>}
        {loading ? (
          <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#FF6600] border-t-transparent mx-auto"></div></div>
        ) : visibleJobs.length === 0 ? (
          <div className="text-center py-12 text-gray-500"><p className="text-base">No jobs at your stage right now</p><p className="text-xs mt-1">Auto-refreshes every 30s</p></div>
        ) : (
          <div className="space-y-3">
            {visibleJobs.map((j) => (
              <button key={j.id} onClick={() => setSelectedId(j.id)}
                className="w-full text-left bg-[#2a2a2a] rounded-xl p-4 border border-gray-800 active:bg-[#333] transition-colors" data-testid={`op-job-${j.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono text-sm font-medium">{j.job_number}</span>
                  {j.dispatch_day && <span className="text-[10px] uppercase text-blue-400">{j.dispatch_day.slice(0, 3)} · {j.target_dispatch}</span>}
                </div>
                <p className="text-sm truncate">{j.customer}</p>
                <div className="mt-2 inline-block text-[11px] px-2 py-1 rounded-full bg-amber-900/40 text-amber-300">Current: {stageLabel(j.current_stage)}</div>
                <p className="text-[10px] text-gray-500 mt-1">{j.units} units · {(j.items || []).length} items</p>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function JobPanel({ jobId, operatorName, onBack }) {
  const [job, setJob] = useState(null);
  const [op, setOp] = useState('');
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    getProductionJob(jobId).then((r) => setJob(r.data)).catch(() => toast.error('Failed to load job'));
  }, [jobId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setOp(operatorName || ''); }, [operatorName]);

  const stages = job ? job.stages : [];
  const cur = job ? stages.find((s) => s.key === job.current_stage) : null;
  const def = cur ? stageDef(cur.key) : null;
  const options = def && def.options ? def.options : [];

  const setChoice = (val) => {
    updateProdJobStage(job.id, cur.key, { choice: val }).then((r) => setJob(r.data)).catch(() => toast.error('Failed to save'));
  };
  const approve = () => {
    updateProdJobStage(job.id, cur.key, { approved: true }).then((r) => { setJob(r.data); toast.success('Approved'); }).catch(() => toast.error('Failed to approve'));
  };
  const upload = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true);
    uploadJobStageFile(job.id, cur.key, file)
      .then((r) => { setJob(r.data); toast.success('Uploaded'); })
      .catch(() => toast.error('Upload failed'))
      .finally(() => { setUploading(false); e.target.value = ''; });
  };
  const advance = () => {
    setBusy(true);
    const isLast = cur.key === 'qc';
    advanceProdJobStage(job.id, { operator: op || null })
      .then((r) => { setJob(r.data); toast.success(isLast ? 'Job completed' : 'Advanced to ' + stageLabel(r.data.current_stage)); })
      .catch((e) => toast.error(e.response ? e.response.data.detail : 'Cannot advance'))
      .finally(() => setBusy(false));
  };

  const pickingReady = job && job.picking_status === 'completed';

  return (
    <div className="min-h-screen bg-[#1a1a1a] text-white">
      <div className="sticky top-0 z-10 bg-[#1a1a1a] border-b border-gray-800 px-4 py-3">
        <button onClick={onBack} className="text-sm text-[#FF6600] mb-1 active:opacity-70" data-testid="op-back-btn">← Back to Jobs</button>
        {job && <><h1 className="font-bold text-base">{job.job_number}</h1><p className="text-xs text-gray-500">{job.customer} · {job.units} units</p></>}
      </div>

      {job && (
        <div className="px-4 py-4 space-y-4">
          {job.status === 'completed' ? (
            <div className="bg-emerald-900/30 border border-emerald-800 rounded-xl p-4 text-center text-emerald-300">Job completed — ready for dispatch</div>
          ) : (
            <div className="bg-[#2a2a2a] rounded-xl border border-amber-800/50 p-4" data-testid="op-current-stage">
              <p className="text-[11px] text-gray-400">Current stage</p>
              <h2 className="text-lg font-bold text-amber-300">{stageLabel(cur.key)}</h2>

              {def && def.kind === 'picking' && (
                <p className={`mt-2 text-sm ${pickingReady ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {job.picking_number ? `Pick list ${job.picking_number}: ${job.picking_status}` : 'No pick list linked'}
                  {!pickingReady && <span className="block text-xs text-gray-400 mt-1">The office must complete the pick list before this can advance.</span>}
                </p>
              )}

              {def && def.kind === 'upload_approve' && (
                <div className="mt-3 space-y-2">
                  <label className="flex items-center justify-center gap-2 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-bold active:scale-95 transition-transform" data-testid="op-upload">
                    {uploading ? 'Uploading…' : (cur.file ? 'Replace document' : 'Upload document')}
                    <input type="file" className="hidden" onChange={upload} disabled={uploading} />
                  </label>
                  {cur.file && <p className="text-xs text-gray-400">Uploaded: {cur.file.file_name}</p>}
                  {cur.file && !cur.approved && (
                    <button onClick={approve} className="w-full py-2.5 rounded-lg bg-emerald-700 text-white text-sm font-bold active:scale-95" data-testid="op-approve">Approve</button>
                  )}
                  {cur.approved && <p className="text-xs text-emerald-400">Approved ✓</p>}
                </div>
              )}

              {def && def.kind === 'choice' && (
                <div className="mt-3 grid grid-cols-1 gap-2">
                  {options.map((o) => (
                    <button key={o.value} onClick={() => setChoice(o.value)}
                      className={`py-2.5 rounded-lg text-sm font-medium border active:scale-95 transition-transform ${cur.choice === o.value ? 'bg-[#FF6600] border-[#FF6600] text-white' : 'bg-[#1a1a1a] border-gray-700 text-gray-300'}`}
                      data-testid={`op-choice-${o.value}`}>{o.label}</button>
                  ))}
                </div>
              )}

              <div className="mt-4 space-y-2">
                <input value={op} onChange={(e) => setOp(e.target.value)} placeholder="Operator name"
                  className="w-full px-3 py-2.5 rounded-lg bg-[#1a1a1a] border border-gray-700 text-white text-sm" data-testid="op-operator" />
                <button onClick={advance} disabled={busy}
                  className="w-full py-3 rounded-lg bg-[#FF6600] text-white font-bold text-base active:scale-95 transition-transform disabled:opacity-50" data-testid="op-advance">
                  {cur.key === 'qc' ? 'Complete Job' : 'Complete Stage & Advance'}
                </button>
              </div>
            </div>
          )}

          <div className="bg-[#2a2a2a] rounded-xl border border-gray-800 divide-y divide-gray-800">
            {stages.map((s) => (
              <div key={s.key} className="px-4 py-2.5 flex items-center justify-between text-sm" data-testid={`op-stage-${s.key}`}>
                <span className={s.status === 'done' ? 'text-emerald-400' : s.status === 'in_progress' ? 'text-amber-300 font-medium' : 'text-gray-500'}>
                  {s.status === 'done' ? '✓ ' : s.status === 'in_progress' ? '● ' : '○ '}{s.label}
                </span>
                {s.operator && <span className="text-[10px] text-gray-500">{s.operator}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default OperatorPage;
